import { Either, Maybe } from 'monet';
import LeftResponse from '@/storage/LeftResponse';

/**
 * @description Контакт.
 * @param {string}  id              Идентификатор
 * @param {string}  name            Название
 * @param {boolean} hasNotification Имеются уведомления
 * @constructor
 */
export function Contact(
  id,
  name,
  hasNotification,
) {
  this.id = id;
  this.name = name;
  this.hasNotification = hasNotification;
}

/**
 * @description Чат
 * @param {string} id
 * @param {string} name
 * @param {array}  messages
 * @constructor
 */
export function Chat(
  id,
  name,
  messages,
) {
  this.id = id;
  this.name = name;
  this.messages = messages;
}

/**
 * @description Запрос на импорт пациентов.
 * @param {string} text Идентификатор клиники.
 * @param {string} chatId Идентификатор клиники.
 * @param {Object} file Файл.
 * @param {string} quotedMessageId Идентификатор цитируемого сообщения.
 * @constructor
 */
export function SendFileRequest(text, chatId, file, quotedMessageId) {
  this.toQuery = () => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('text', text);
    formData.append('chat_id', chatId);
    formData.append('quoted_message_id', quotedMessageId);
    return formData;
  };
}

/**
 * @description Шлюз рабочего графика.
 * @param {Axios} axios HTTP клиент.
 * @constructor
 */
export default function WhatsAppGateway(axios) {
  /**
   * @description Получить список чатов по категории.
   * @param {string} searchQuery
   * @returns {Promise<Either>}
   */
  this.getChatList = (searchQuery) => axios
    .get('/api/whats-app/get-chats', { params: { search_query: searchQuery } })
    .then(
      async (response) => {
        const { data } = response;
        const chatList = data.chats.map(
          (chat) => new Contact(
            chat.id,
            chat.name,
            chat.has_notification,
          ),
        );
        return Either.Right(chatList);
      },
      async (error) => {
        if (error.isAxiosError) {
          if (error.response && [400, 403, 404].includes(error.response.status)) {
            const { causes, title } = error.response.data;
            const leftResponse = new LeftResponse(causes, title);
            return Either.left(leftResponse);
          }
          if (!error.response) {
            throw new Error('Network error: сервер по адресу /api/whats-app/get-chats не ответил на запрос');
          }
        }
        throw error;
      },
    );
  /**
   * @description Получить чат.
   * @param {string} chatId Идентификатор чата.
   * @returns {Promise<any>}
   */
  this.getChat = (chatId) => axios
    .get('/api/whats-app/get-chat', { params: { chat_id: chatId } })
    .then(
      async (response) => {
        const { data } = response;
        const chat = new Chat(
          data.chat.id,
          data.chat.name,
          data.chat.messages.map(
            (message) => ({
              id: message.id,
              isMine: message.is_mine,
              text: message.text,
              from: message.from,
              type: message.type,
              quotedMessage: message.quoted_message?.text,
              downloadUrl: message.download_url,
              fileName: message.file_name,
              options: message
                .options
                ?.map((option) => (
                  { text: option.text, votes: option.votes }
                )),
            }),
          ),
        );
        return Either.Right(chat);
      },
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Отправить Файл.
   * @param {Object} request
   * @returns {Promise<any>}
   */
  this.sendMessage = (request) => axios
    .post('/api/whats-app/chat/send-message', request.toQuery())
    .then(
      async (response) => Either.Right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Удалить сообщение.
   * @param {string} messageId Идентификатор сообщения.
   * @param {string} chatId    Идентификатор чата.
   * @returns {Promise<any>}
   */
  this.deleteMessage = (messageId, chatId) => axios
    .delete('/api/whats-app/chat/delete-message', { params: { message_id: messageId, chat_id: chatId } })
    .then(
      async (response) => Either.Right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Переслать сообщение.
   * @param {string} forwardTo   Источник пересылки.
   * @param {string} forwardFrom Получатель пересылки.
   * @param {string} messageId   Сообщение пересылки.
   * @returns {Promise<any>}
   */
  this.forwardMessage = (forwardTo, forwardFrom, messageId) => axios
    .post(
      '/api/whats-app/chat/forward-message',
      { forward_to: forwardTo, forward_from: forwardFrom, message_id: messageId },
    )
    .then(
      async (response) => Either.Right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Получить количество непрочитанных чатов.
   * @returns {Promise<number>}
   */
  this.viewNewNotificationCount = () => axios.get('/api/whats-app/notifications/count')
    .then(
      async (response) => Maybe.some(response.data.count || 0),
      async () => Maybe.none(),
    );
}
